import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const SouthernCACaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Southern CA – Transformative Writing | Curipod"
      subTitle="How two 5th-grade teachers in Southern California used Curipod to drive 35% growth in ELA — from 51% to 69% meeting or exceeding standards on the CAASPP."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>From 51% to 69%</HeroTitle>
            <HeroSubtitle>
              Transforming Writing Instruction Leads to 35% Growth
            </HeroSubtitle>
            <HeroDescription>
              How two 5th-grade teachers at an elementary school in Southern California
              used Curipod to beat the benchmark — driving 100% student growth and the
              highest gains in the district, in a school where 87% of students qualify for
              free and reduced-price lunch.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Elementary School in Southern CA</MetaBadge>
              <MetaBadge>5th Grade ELA</MetaBadge>
              <MetaBadge>2024–2025 School Year</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    51<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>CAASPP Meeting or Exceeding</StatLabel>
                  <StatYear>2023–2024 · 4th Grade · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    69<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>CAASPP Meeting or Exceeding</StatLabel>
                  <StatYear>2024–2025 · 5th Grade · With Curipod</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem>
                  <StatNumber>
                    66<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>District Benchmark</StatLabel>
                  <StatYear>Expected Score</StatYear>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    68<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Actual CAASPP Score</StatLabel>
                  <StatYear>Exceeded the Benchmark</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* FROM GOALS TO GROWTH */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>FROM GOALS TO GROWTH</SectionTag>
              <SectionTitle>A 5th-Grade Success Story</SectionTitle>
              <SectionText>
                In a school where <strong>87% of students</strong> qualify for free and
                reduced-price lunch and nearly all (<strong>94%</strong>) are Hispanic,
                with many English learners, teachers navigate complex challenges every
                day. And the student outcomes tell a story of resilience.
              </SectionText>
              <SectionText>
                For veteran teacher Ms. M., now in her 25th year, the challenge was
                personal. A seasoned math teacher, she was also responsible for 5th-grade
                ELA. Ms. M. and her teaching partner knew that writing and feedback would
                be central to helping students succeed on the CAASPP — and that
                traditional methods alone wouldn&apos;t be enough.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  I cannot do it physically. I cannot give immediate feedback to 30
                  students at once, but with Curipod, it is possible. I wish I had this 20
                  years ago.
                </QuoteText>
                <QuoteAttrib>— Ms. M., 5th Grade Teacher</QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamPhotoArea>
                <CircularImageWrap $size={150}>
                  <Image
                    src="/case-study-southern-ca/students-classroom.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Students using Curipod in the classroom"
                  />
                </CircularImageWrap>
                <TeamPhotoCaption>5th-grade students using Curipod</TeamPhotoCaption>
              </TeamPhotoArea>
              <TeamCardTitle>School Profile</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>Location</TeamRole>
                  <TeamName>Elementary School in Southern CA</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Demographics</TeamRole>
                  <TeamName>87% FRPL, 94% Hispanic</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Grade &amp; Subject</TeamRole>
                  <TeamName>5th Grade ELA</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Focus Areas</TeamRole>
                  <TeamName>
                    Student &amp; teacher confidence, real-time feedback, closing gaps for
                    ELs
                  </TeamName>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* A BREAKTHROUGH YEAR */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              A BREAKTHROUGH YEAR
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              100% of Students Show Growth with Curipod
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Ms. M. and her teaching partner integrated Curipod 2–3 times a week for
              short constructed response (SCR) practice leading up to CAASPP. They tapped
              into student choice by encouraging students to write about topics they
              loved, which kept engagement high.
            </SectionText>

            <FocusAreaGrid>
              {[
                'Student & Teacher Confidence',
                'Real-time Feedback',
                'Closing Gaps for ELs',
              ].map(area => (
                <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
              ))}
            </FocusAreaGrid>

            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '40px auto 0' }}
            >
              With Curipod&apos;s real-time, targeted feedback, students could write,
              reflect, and revise immediately, strengthening their arguments and evidence
              on the spot. By the end of the 2024–25 school year, results from the
              5th-grade team stood out across the district.
            </SectionText>

            <ScreenshotGrid>
              <ScreenshotCard>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-southern-ca/curipod-read-passage.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod lesson — Read the Passage activity"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>
                  Engaging passages keep students motivated
                </ScreenshotCaption>
              </ScreenshotCard>
              <ScreenshotCard>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-southern-ca/curipod-ai-feedback.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod AI feedback on student writing"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>
                  Real-time AI feedback on every response
                </ScreenshotCaption>
              </ScreenshotCard>
            </ScreenshotGrid>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* THE CURIPOD SHIFT — 3 IMPACT AREAS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            THE CURIPOD SHIFT
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            Three Ways Curipod Changed the Classroom
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Specific, Actionable Guidance</ImpactTitle>
              <ImpactQuote>
                &ldquo;It&apos;s super targeted … the feedback said, &lsquo;Make sure your
                evidence supports your main idea sentence,&rsquo; and we went back and
                found a better one.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Ms. M., 5th Grade Teacher</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>⭐</ImpactIcon>
              <ImpactTitle>Instant Confidence-Building</ImpactTitle>
              <ImpactQuote>
                &ldquo;Some kids just will say, &lsquo;I&apos;m a struggling
                writer,&rsquo; but when you get the instantaneous feedback … it&apos;s
                very specific. It builds their confidence.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Ms. M., 5th Grade Teacher</ImpactAttrib>
            </ImpactCard>

            <ImpactCard $wide>
              <ImpactIcon>🚀</ImpactIcon>
              <ImpactTitle>Student Breakthroughs</ImpactTitle>
              <ImpactQuote>
                &ldquo;I had a student who was not meeting standards in fourth grade and
                then got a perfect score on state testing in 5th … it had to be from how
                they were getting instantaneous feedback.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Ms. M., 5th Grade Teacher</ImpactAttrib>
            </ImpactCard>
          </ImpactGrid>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              GROWTH THAT SPEAKS FOR ITSELF
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              Exceeding the District Benchmark
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              In ELA, the percentage of students meeting or exceeding standards on the
              CAASPP jumped from <strong>50.7% in 4th grade to 68.6% in 5th grade</strong>
              . Even more telling, while other grade levels scored lower on CAASPP than on
              their district benchmark, these students exceeded it — scoring 66% on the
              district benchmark and <strong>68% on CAASPP</strong>.
            </SectionText>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                It&apos;s the most revolutionary tool in my teaching career.
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Ms. M., 5th Grade Teacher
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              Teachers credited one factor above all others: consistent use of Curipod for
              writing practice and <strong>real-time feedback</strong>. Not only did this
              group of 5th graders surpass district averages, but they also delivered some
              of the highest gains in the entire district.
            </ResultNote>

            <ResultsStudentsPhoto>
              <CircularImageWrap $size={160} $borderColor="rgba(249,151,70,0.6)">
                <Image
                  src="/case-study-southern-ca/students-classroom.png"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center"
                  alt="5th-grade students using Curipod"
                />
              </CircularImageWrap>
              <ResultsPhotoLabel>5th-grade students using Curipod</ResultsPhotoLabel>
            </ResultsStudentsPhoto>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Future-Ready Learning
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              At this school, the 5th-grade team proved that growth is possible when the
              right tools meet strong teaching. With Curipod, students gained confidence,
              sharpened their writing, and exceeded district benchmarks. Ms. M. sees
              Curipod not just as a test prep tool but as a foundation for future-ready
              learning.
            </SectionText>

            <PeoplePhotoRow>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-southern-ca/students-classroom.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Students in the classroom"
                  />
                </CircularImageWrap>
                <PhotoLabel>Students in class</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <ScreenshotCircleWrap $size={110}>
                  <Image
                    src="/case-study-southern-ca/curipod-writing-responses.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Student writing responses in Curipod"
                  />
                </ScreenshotCircleWrap>
                <PhotoLabel>Writing responses</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <ScreenshotCircleWrap $size={110}>
                  <Image
                    src="/case-study-southern-ca/curipod-ai-feedback.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="AI feedback"
                  />
                </ScreenshotCircleWrap>
                <PhotoLabel>AI feedback</PhotoLabel>
              </PhotoItem>
            </PeoplePhotoRow>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                It&apos;s definitely 21st century learning … moving forward into the rest
                of their education, technology can be an asset … it&apos;s not cheating,
                you use it appropriately and it&apos;s a powerful tool.
              </FinalQuoteText>
              <QuoteAttrib>— Ms. M., 5th Grade Teacher</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {['Feedback', 'Confidence', 'Writing Growth', 'Equity'].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Southern CA case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Southern_CA.pdf"
              download="Curipod_Case_Study_Southern_CA.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default SouthernCACaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CIRCULAR IMAGE ─────────────────────────────────────────────────────────── */

const CircularImageWrap = styled.div<{ $size: number; $borderColor?: string }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid ${({ $borderColor }) => $borderColor ?? theme.colors.orange};
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

const ScreenshotCircleWrap = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid ${theme.colors.orange};
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamPhotoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid ${theme.colors.beigeDark};
`;

const TeamPhotoCaption = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

/* ─── SCREENSHOT GRID ────────────────────────────────────────────────────────── */

const ScreenshotGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 48px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ScreenshotCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 3px solid ${theme.colors.orange};
`;

const ScreenshotImageWrap = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56%;
  border-radius: 8px;
  overflow: hidden;
`;

const ScreenshotCaption = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  text-align: center;
  margin: 12px 0 0;
  font-style: italic;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0 0 48px;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const ResultsStudentsPhoto = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ResultsPhotoLabel = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const PeoplePhotoRow = styled.div`
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 48px;

  @media ${MOBILE} {
    gap: 20px;
    margin-bottom: 32px;
  }
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PhotoLabel = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
